import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import OrnateDivider from '../components/OrnateDivider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [blog, setBlog] = useState(true);
  const [reeng, setReeng] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setError('No unsubscribe token provided.'); setLoading(false); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/unsubscribe/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setUser({ email: res.data.email, name: res.data.name });
        setBlog(Boolean(res.data.blog_notifications));
        setReeng(Boolean(res.data.practice_reminders));
      } catch (e) {
        setError(e?.response?.data?.detail || 'This unsubscribe link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setSavedAt(null);
    try {
      await axios.put(`${API}/unsubscribe/${encodeURIComponent(token)}`, {
        blog_notifications: blog,
        practice_reminders: reeng,
      });
      setSavedAt(new Date().toISOString());
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save preferences.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen mystical-bg pt-28 pb-20 px-4">
      <div className="max-w-2xl mx-auto" data-testid="unsubscribe-page">
        <OrnateDivider label="Email Preferences" variant="diamond" className="mb-6 max-w-xl mx-auto" />
        <h1 className="text-3xl sm:text-4xl font-heading font-bold golden-text text-glow tracking-wider text-center mb-6">
          Manage your email preferences
        </h1>

        <div className="card-mystical rounded-2xl p-6 sm:p-10">
          {loading && (
            <div className="flex items-center justify-center py-10 text-primary/70">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading your preferences…
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center text-center py-6" data-testid="unsubscribe-error">
              <AlertCircle className="text-primary/70 mb-3" size={28} />
              <p className="text-primary/85 mb-2">{error}</p>
              <p className="text-[12px] text-primary/55 italic">
                If you believe this is wrong, write to us from the address you registered with.
              </p>
            </div>
          )}

          {!loading && !error && user && (
            <>
              <p className="text-primary/80 mb-6">
                Signed in as <strong className="text-primary">{user.email}</strong>.
                Choose which emails you'd like to keep receiving.
              </p>

              <label className="flex items-start gap-3 p-4 rounded-xl border border-primary/15 bg-primary/[0.03] mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={blog}
                  onChange={(e) => setBlog(e.target.checked)}
                  className="mt-1 accent-amber-500"
                  data-testid="unsubscribe-blog"
                />
                <span>
                  <span className="block text-primary/95 font-heading tracking-wide">
                    Blog and article notifications
                  </span>
                  <span className="block text-[13px] text-primary/65 mt-1">
                    New posts from MyShaktiverse.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-3 p-4 rounded-xl border border-primary/15 bg-primary/[0.03] mb-5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={reeng}
                  onChange={(e) => setReeng(e.target.checked)}
                  className="mt-1 accent-amber-500"
                  data-testid="unsubscribe-reengagement"
                />
                <span>
                  <span className="block text-primary/95 font-heading tracking-wide">
                    Practice reminders and re-engagement
                  </span>
                  <span className="block text-[13px] text-primary/65 mt-1">
                    Gentle nudges when you've been away from the practice.
                  </span>
                </span>
              </label>

              <p className="text-[12px] text-primary/55 italic mb-6">
                Practice milestones and account emails (welcome, enrollment confirmations,
                password resets) will always be sent as they relate to your activity.
              </p>

              <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
                {savedAt ? (
                  <span className="flex items-center gap-2 text-[13px] text-amber-300/90" data-testid="unsubscribe-saved">
                    <CheckCircle2 size={14} /> Preferences saved
                  </span>
                ) : <span />}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="ornate-button inline-flex items-center gap-2 px-6 py-3 text-sm sm:text-base disabled:opacity-50"
                  data-testid="unsubscribe-save"
                >
                  {saving ? <Loader2 className="animate-spin" size={14} /> : null}
                  Save preferences
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
